// Combat: allies auto-target nearest enemy in range and fire pooled
// projectiles. Enemies behave per their EnemyKindComp: seeker/swarmer/elite
// melee, shooter stops at range and fires, bomber charges then detonates.
//
// Effect wiring (audit-tracked):
//   - Berserker      ally damage boost when low HP
//   - Glass Cannon   pierce on projectiles
//   - Iron Hide      flat damage reduction (melee, projectile, bomber blast)
//   - Pack Leader    nearby allies deal more damage
//   - Frostbitten    chance to apply Slow on hit
//   - Pulsar         periodic AOE pulse damage to enemies
//   - armor trait    base flat damage reduction (stacks with Iron Hide)
//   - SlowComp       reduces enemy moveSpeed by `factor` while remaining > 0
//   - crit trait     chance to deal (1 + effectiveCrit)x damage per projectile
//   - evasion trait  chance to dodge incoming damage entirely

import type { WorldState } from '../world-state';
import { C } from '../components';
import type {
  Position,
  Velocity,
  AttackComp,
  FactionComp,
  Health,
  Radius,
  Projectile,
  EmergentComp,
  EnemyKindComp,
  BomberComp,
  Stats,
  SlowComp,
  PulseTimerComp,
  XpComp,
} from '../components';
import { spawnProjectile } from '../spawn';
import { chance } from '../rng';
import type { ComponentStore } from '../ecs';

const BOMBER_FUSE_SEC = 0.7;
const PACK_LEADER_SCAN = 130; // search radius for any pack-leader aura

export function combatSystem(ws: WorldState, dt: number): void {
  const { world } = ws;
  const positions = world.store<Position>(C.position);
  const velocities = world.store<Velocity>(C.velocity);
  const attacks = world.store<AttackComp>(C.attack);
  const factions = world.store<FactionComp>(C.faction);
  const healths = world.store<Health>(C.health);
  const radii = world.store<Radius>(C.radius);
  const emergents = world.store<EmergentComp>(C.emergent);
  const kinds = world.store<EnemyKindComp>(C.enemyKind);
  const bombers = world.store<BomberComp>(C.bomber);
  const stats = world.store<Stats>(C.stats);
  const slows = world.store<SlowComp>(C.slow);
  const pulseTimers = world.store<PulseTimerComp>(C.pulseTimer);
  const xps = world.store<XpComp>(C.xp);

  // Rebuild both spatial hashes (defensive — combat usable without flocking).
  ws.enemyHash.clear();
  ws.allyHash.clear();
  positions.forEach((pos, id) => {
    const side = factions.get(id)?.side;
    if (side === 'enemy') ws.enemyHash.insert(id, pos.x, pos.y);
    else if (side === 'ally') ws.allyHash.insert(id, pos.x, pos.y);
  });

  // Ally auto-attack.
  attacks.forEach((atk, id) => {
    if (factions.get(id)?.side !== 'ally') return;
    atk.cooldown -= dt;
    if (atk.cooldown > 0) return;
    const pos = positions.get(id);
    if (!pos) return;

    let bestId = -1;
    let bestDist = atk.range;
    ws.enemyHash.forEachInRadius(pos.x, pos.y, atk.range, (eid) => {
      const ep = positions.get(eid);
      if (!ep) return;
      const d = Math.hypot(ep.x - pos.x, ep.y - pos.y);
      if (d < bestDist) {
        bestDist = d;
        bestId = eid;
      }
    });
    if (bestId < 0) return;

    const tp = positions.get(bestId)!;
    const dx = tp.x - pos.x;
    const dy = tp.y - pos.y;
    const len = Math.hypot(dx, dy) || 1;

    let damage = atk.damage;
    let pierce = 0;
    let freeze: { chance: number; duration: number } | undefined;
    const e = emergents.get(id);
    if (e) {
      const rage = e.effect.rageBonus;
      if (rage) {
        const h = healths.get(id);
        if (h && h.current / h.max <= rage.hpThreshold) damage *= rage.damageMul;
      }
      if (e.effect.pierce) pierce = e.effect.pierce;
      if (e.effect.freeze) freeze = e.effect.freeze;
    }

    // Pack Leader aura — scan allies in range for a packLeader effect and
    // multiply our outgoing damage by it.
    const auraMul = packLeaderAuraAt(ws, pos.x, pos.y, id, emergents);
    if (auraMul > 1) damage *= auraMul;

    // Critical hit roll — effective crit capped at 0.95.
    const allyStats = stats.get(id);
    if (allyStats) {
      const effectiveCrit = Math.min(allyStats.sheet.crit, 0.95);
      if (effectiveCrit > 0 && chance(ws.rng('combat'), effectiveCrit)) {
        damage *= 1 + effectiveCrit;
      }
    }

    spawnProjectile(ws, {
      ownerId: id,
      side: 'ally',
      x: pos.x,
      y: pos.y,
      vx: (dx / len) * atk.projectileSpeed,
      vy: (dy / len) * atk.projectileSpeed,
      damage,
      pierce,
      ...(freeze ? { freeze } : {}),
    });
    atk.cooldown = 1 / Math.max(0.1, atk.rate);
  });

  // Pulsar: timed AOE damage.
  pulseTimers.forEach((pt, id) => {
    pt.remaining -= dt;
    if (pt.remaining > 0) return;
    const pos = positions.get(id);
    if (!pos) {
      pt.remaining = pt.intervalSec;
      return;
    }
    ws.enemyHash.forEachInRadius(pos.x, pos.y, pt.radius, (eid) => {
      const ep = positions.get(eid);
      if (!ep) return;
      const d = Math.hypot(ep.x - pos.x, ep.y - pos.y);
      if (d <= pt.radius) {
        const h = healths.get(eid);
        if (h) {
          const hpBefore = h.current;
          h.current -= pt.damage;
          if (hpBefore > 0 && h.current <= 0) grantXpToAllies(h.max, xps, stats);
        }
      }
    });
    pt.remaining = pt.intervalSec;
  });

  // Enemy behavior: dispatch per kind.
  attacks.forEach((atk, id) => {
    if (factions.get(id)?.side !== 'enemy') return;
    const pos = positions.get(id);
    const vel = velocities.get(id);
    if (!pos || !vel) return;

    // Find nearest ally.
    let bestAid = -1;
    let bestDist = Infinity;
    let bestX = 0;
    let bestY = 0;
    ws.allyHash.forEachInRadius(pos.x, pos.y, 1500, (aid) => {
      const ap = positions.get(aid);
      if (!ap) return;
      const d = Math.hypot(ap.x - pos.x, ap.y - pos.y);
      if (d < bestDist) {
        bestDist = d;
        bestAid = aid;
        bestX = ap.x;
        bestY = ap.y;
      }
    });
    if (bestAid < 0) {
      bestX = 0;
      bestY = 0;
      bestDist = Math.hypot(pos.x, pos.y) || 1;
    }

    const kind = kinds.get(id)?.kind ?? 'seeker';
    const dx = bestX - pos.x;
    const dy = bestY - pos.y;
    const len = Math.hypot(dx, dy) || 1;
    const ndx = dx / len;
    const ndy = dy / len;
    let moveSpeed = enemyBaseSpeed(kind);
    // SlowComp on enemy: scale speed by factor while active.
    const slow = slows.get(id);
    if (slow) moveSpeed *= slow.factor;

    if (kind === 'shooter') {
      if (bestDist < atk.range * 0.8) moveSpeed = 0;
      atk.cooldown -= dt;
      if (atk.cooldown <= 0 && bestDist <= atk.range && bestAid >= 0) {
        spawnProjectile(ws, {
          ownerId: id,
          side: 'enemy',
          x: pos.x,
          y: pos.y,
          vx: ndx * atk.projectileSpeed,
          vy: ndy * atk.projectileSpeed,
          damage: atk.damage,
          pierce: 0,
        });
        atk.cooldown = 1 / Math.max(0.1, atk.rate);
      }
    } else if (kind === 'bomber') {
      const b = bombers.get(id);
      if (b) {
        if (bestDist <= atk.range && b.fuse === Infinity) b.fuse = BOMBER_FUSE_SEC;
        if (b.fuse < Infinity) {
          b.fuse -= dt;
          moveSpeed *= 1.4;
          if (b.fuse <= 0) {
            ws.allyHash.forEachInRadius(pos.x, pos.y, b.blastRadius, (aid) => {
              const ap = positions.get(aid);
              if (!ap) return;
              const d = Math.hypot(ap.x - pos.x, ap.y - pos.y);
              if (d <= b.blastRadius) {
                applyDamageToAlly(aid, b.blastDamage, healths, emergents, stats, ws);
              }
            });
            const h = healths.get(id);
            if (h) h.current = 0;
          }
        }
      }
    } else {
      // Melee: seeker, swarmer, elite.
      atk.cooldown -= dt;
      if (atk.cooldown <= 0 && bestDist <= atk.range && bestAid >= 0) {
        applyDamageToAlly(bestAid, atk.damage, healths, emergents, stats, ws);
        atk.cooldown = 1 / Math.max(0.1, atk.rate);
      }
    }

    vel.vx = ndx * moveSpeed;
    vel.vy = ndy * moveSpeed;
    pos.x += vel.vx * dt;
    pos.y += vel.vy * dt;
  });

  projectileSystem(ws, dt);

  positions.forEach((pos, id) => {
    if (factions.get(id)?.side !== 'enemy') return;
    if (Math.abs(pos.x) > ws.bounds.maxX + 600 || Math.abs(pos.y) > ws.bounds.maxY + 600) {
      world.destroyEntity(id);
    }
  });

  void radii;
}

function applyDamageToAlly(
  allyId: number,
  rawDamage: number,
  healths: ComponentStore<Health>,
  emergents: ComponentStore<EmergentComp>,
  stats: ComponentStore<Stats>,
  ws: WorldState,
): void {
  const h = healths.get(allyId);
  if (!h) return;
  const s = stats.get(allyId);
  // Evasion dodge check — skip damage entirely on success.
  if (s) {
    const effectiveEvasion = Math.min(s.sheet.evasion, 0.6);
    if (effectiveEvasion > 0 && chance(ws.rng('combat'), effectiveEvasion)) return;
  }
  const e = emergents.get(allyId);
  let dmg = rawDamage;
  const armor = s?.sheet.armor ?? 0;
  const hide = e?.effect.ironHide?.reduction ?? 0;
  const total = armor + hide;
  if (total > 0) dmg = Math.max(0.1, dmg - total);
  h.current -= dmg;
}

function grantXpToAllies(
  xpReward: number,
  xps: ComponentStore<XpComp>,
  stats: ComponentStore<Stats>,
): void {
  xps.forEach((xpComp, aid) => {
    const xpGain = stats.get(aid)?.sheet.xpGain ?? 1;
    xpComp.xp += xpReward * xpGain;
  });
}

function packLeaderAuraAt(
  ws: WorldState,
  x: number,
  y: number,
  selfId: number,
  emergents: ComponentStore<EmergentComp>,
): number {
  let mul = 1;
  ws.allyHash.forEachInRadius(x, y, PACK_LEADER_SCAN, (aid) => {
    if (aid === selfId) return;
    const pl = emergents.get(aid)?.effect.packLeader;
    if (!pl) return;
    mul = Math.max(mul, pl.damageMul);
  });
  return mul;
}

function enemyBaseSpeed(kind: string): number {
  switch (kind) {
    case 'swarmer':
      return 85;
    case 'bomber':
      return 70;
    case 'seeker':
      return 55;
    case 'elite':
      return 38;
    case 'shooter':
      return 30;
    default:
      return 55;
  }
}

function projectileSystem(ws: WorldState, dt: number): void {
  const { world } = ws;
  const positions = world.store<Position>(C.position);
  const velocities = world.store<Velocity>(C.velocity);
  const projectiles = world.store<Projectile>(C.projectile);
  const healths = world.store<Health>(C.health);
  const factions = world.store<FactionComp>(C.faction);
  const radii = world.store<Radius>(C.radius);
  const emergents = world.store<EmergentComp>(C.emergent);
  const stats = world.store<Stats>(C.stats);
  const slows = world.store<SlowComp>(C.slow);
  const xps = world.store<XpComp>(C.xp);

  const toRemove: number[] = [];
  projectiles.forEach((p, id) => {
    p.ttl -= dt;
    if (p.ttl <= 0) {
      toRemove.push(id);
      return;
    }
    const pos = positions.get(id);
    const vel = velocities.get(id);
    if (!pos || !vel) return;
    pos.x += vel.vx * dt;
    pos.y += vel.vy * dt;

    const targetHash = p.side === 'ally' ? ws.enemyHash : ws.allyHash;
    const startPierce = p.pierce;
    let usedPierce = 0;
    let consumed = false;
    targetHash.forEachInRadius(pos.x, pos.y, 8, (tid) => {
      if (consumed) return;
      if (p.hits.has(tid)) return;
      if (factions.get(tid)?.side === p.side) return;
      const tp = positions.get(tid);
      const tr = radii.get(tid);
      if (!tp || !tr) return;
      const d = Math.hypot(tp.x - pos.x, tp.y - pos.y);
      if (d <= tr.r + 2) {
        let dmg = p.damage;
        // Enemy → ally: evasion dodge check then armor reduction.
        if (p.side === 'enemy') {
          const s = stats.get(tid);
          const effectiveEvasion = Math.min(s?.sheet.evasion ?? 0, 0.6);
          if (effectiveEvasion > 0 && chance(ws.rng('combat'), effectiveEvasion)) {
            // Dodged — consume the projectile but skip HP reduction.
            p.hits.add(tid);
            ws.hitCount++;
            usedPierce++;
            if (usedPierce > startPierce) {
              toRemove.push(id);
              consumed = true;
            }
            return;
          }
          const e = emergents.get(tid);
          const armor = s?.sheet.armor ?? 0;
          const hide = e?.effect.ironHide?.reduction ?? 0;
          const total = armor + hide;
          if (total > 0) dmg = Math.max(0.1, dmg - total);
        }
        const h = healths.get(tid);
        const hpBefore = h?.current ?? 0;
        if (h) h.current -= dmg;
        // XP grant: ally projectile delivers killing blow to enemy.
        if (p.side === 'ally' && h && hpBefore > 0 && h.current <= 0) {
          grantXpToAllies(h.max, xps, stats);
        }
        p.hits.add(tid);
        ws.hitCount++;

        // Frostbitten freeze on enemy targets.
        if (p.side === 'ally' && p.freeze) {
          if (chance(ws.rng('combat'), p.freeze.chance)) {
            const existing = slows.get(tid);
            const factor = 0.4;
            if (!existing || existing.remaining < p.freeze.duration) {
              slows.set(tid, { remaining: p.freeze.duration, factor });
            }
          }
        }

        usedPierce++;
        if (usedPierce > startPierce) {
          toRemove.push(id);
          consumed = true;
        }
      }
    });
  });
  for (const id of toRemove) world.destroyEntity(id);
}
